/// <reference path="../types/interfaces.d.ts" />

import BabbageGo from '@babbage/go';
import { WalletClient } from '@bsv/sdk/wallet';
import { P2PKH } from '@bsv/sdk';
import type { PaymentConfig, PaymentRecord, BSVPayments } from '../types/interfaces';

// HandCash wallet address for receiving all payments
const HANDCASH_ADDRESS = '1CBXFc7cxByahSYC4iUyNw2atbqUnJL1fb';

const PAYMENT_CONFIG: PaymentConfig = {
  VIEW_DETAILS: 250,
  VIEW_PREDICTIONS: 50,
  ADD_TO_PORTFOLIO: 500,
  WATCHLIST: 50,
  CURRENCY_UNLOCK: 50,
  PRICE_ALERT: 100,
  PRICE_ALERT_EMAIL: 50,
  PORTFOLIO_ANALYTICS: 500,
  EXPORT_DATA: 200,
  PRICE_COMPARISON: 300,
  ADVANCED_PORTFOLIO_ANALYTICS: 1000,
  MULTIPLE_PORTFOLIO: 10000,
  REFRESH_DATA: 10,
  COIN_DESCRIPTION: 100,
  MERCHANT_ADDRESS: HANDCASH_ADDRESS,
  API_BASE: 'https://api.whatsonchain.com/v1/bsv/main',
  POLL_INTERVAL: 2000,
  POLL_MAX_ATTEMPTS: 30
};

let babbageGo: BabbageGo | null = null;

function getBabbageGo(): BabbageGo {
  if (!babbageGo) {
    // Use 'auto' mode to automatically detect desktop or browser wallet
    // This will detect MetaNet wallet on desktop, browser extensions, etc.
    babbageGo = new BabbageGo(new WalletClient('auto'), {
      showModal: false, // Don't show automatic modals (wallet will show its own)
      hangOnWalletErrors: false, // Don't hang on errors - fail gracefully
    });
  }
  return babbageGo;
}

async function requestPayment(action: string, amount: number): Promise<PaymentRecord | null> {
  try {
    const bg = getBabbageGo();
    
    // Convert BSV address to proper P2PKH locking script
    const merchantAddress = PAYMENT_CONFIG.MERCHANT_ADDRESS;
    const p2pkh = new P2PKH();
    const lockingScriptObj = p2pkh.lock(merchantAddress);
    const lockingScript = lockingScriptObj.toHex();
    
    // Create action - BabbageGo will handle wallet connection, funding, etc.
    let actionResult;
    try {
      actionResult = await bg.createAction({
        description: action === 'DONATE' ? 'Donation to CryptoScope' : `Payment for ${action}`,
        outputs: [{
          satoshis: amount,
          lockingScript: lockingScript,
          outputDescription: action === 'DONATE' ? 'Donation to CryptoScope' : `Payment for ${action}`
        }]
      });
    } catch (createError: any) {
      // Check if it's a connection error
      const errorMessage = createError?.message || String(createError);
      const errorString = JSON.stringify(createError);
      
      if (errorMessage.includes('CONNECTION_REFUSED') || 
          errorMessage.includes('ERR_CONNECTION_REFUSED') ||
          errorMessage.includes('getVersion') ||
          errorMessage.includes('ECONNREFUSED') ||
          errorString.includes('CONNECTION_REFUSED') ||
          errorString.includes('ERR_CONNECTION_REFUSED')) {
        throw new Error('Wallet not found. Please make sure MetaNet wallet is running and try again.');
      }
      // Re-throw other errors
      throw createError;
    }

    // If actionResult is null/undefined, wallet connection likely failed
    if (!actionResult) {
      throw new Error('Wallet not found. Please make sure MetaNet wallet is running and try again.');
    }

    // Handle different response formats from createAction
    // If we got a txid, payment was successful
    if (actionResult?.txid) {
      const paymentRecord: PaymentRecord = {
        action,
        amount,
        timestamp: Date.now(),
        txId: actionResult.txid,
        protocol: 'BRC-100'
      };

      const payments = JSON.parse(localStorage.getItem('bsvPayments') || '[]');
      payments.push(paymentRecord);
      localStorage.setItem('bsvPayments', JSON.stringify(payments));

      return paymentRecord;
    }
    
    // If we got a signable transaction, we need to sign it using signAction
    // This will trigger the wallet UI for user approval
    if (actionResult?.signableTransaction) {
      try {
        // Sign the transaction - this will show wallet UI and handle broadcasting
        const signResult = await bg.signAction({
          signableTransaction: actionResult.signableTransaction
        });

        // If signing was successful and we got a txid
        if (signResult?.txid) {
          const paymentRecord: PaymentRecord = {
            action,
            amount,
            timestamp: Date.now(),
            txId: signResult.txid,
            protocol: 'BRC-100'
          };

          const payments = JSON.parse(localStorage.getItem('bsvPayments') || '[]');
          payments.push(paymentRecord);
          localStorage.setItem('bsvPayments', JSON.stringify(payments));

          return paymentRecord;
        }

        // If we got a transaction object from signing
        if (signResult?.tx) {
          const paymentRecord: PaymentRecord = {
            action,
            amount,
            timestamp: Date.now(),
            txId: 'completed_' + Date.now(),
            protocol: 'BRC-100'
          };

          const payments = JSON.parse(localStorage.getItem('bsvPayments') || '[]');
          payments.push(paymentRecord);
          localStorage.setItem('bsvPayments', JSON.stringify(payments));

          return paymentRecord;
        }

        // Signing was cancelled or failed
        return null;
      } catch (signError) {
        // User cancelled or signing failed
        return null;
      }
    }
    
    // If we got a transaction object directly
    if (actionResult?.tx) {
      // Try to extract txid from transaction
      // Note: AtomicBEEF format needs parsing, but for now we'll use a placeholder
      const paymentRecord: PaymentRecord = {
        action,
        amount,
        timestamp: Date.now(),
        txId: 'completed_' + Date.now(),
        protocol: 'BRC-100'
      };

      const payments = JSON.parse(localStorage.getItem('bsvPayments') || '[]');
      payments.push(paymentRecord);
      localStorage.setItem('bsvPayments', JSON.stringify(payments));

      return paymentRecord;
    }

    // No valid result - payment failed or was cancelled
    return null;
  } catch (error) {
    // Re-throw wallet connection errors so they can be displayed to the user
    if (error instanceof Error && 
        (error.message.includes('Wallet not found') || 
         error.message.includes('CONNECTION_REFUSED') ||
         error.message.includes('ERR_CONNECTION_REFUSED'))) {
      throw error;
    }
    // Silent error handling for other errors (user cancellation, etc.)
    return null;
  }
}

function hasPaidForAction(action: string): boolean {
  const payments = JSON.parse(localStorage.getItem('bsvPayments') || '[]');
  const oneHour = 60 * 60 * 1000;
  const now = Date.now();

  return payments.some((payment: PaymentRecord) => {
    if (payment.action !== action) return false;
    return (now - payment.timestamp) < oneHour;
  });
}

function getPaymentAmount(action: string): number {
  const actionKey = action as keyof PaymentConfig;
  if (actionKey in PAYMENT_CONFIG && typeof PAYMENT_CONFIG[actionKey] === 'number') {
    return PAYMENT_CONFIG[actionKey] as number;
  }
  return 0;
}

export function initializeBlockchain(): void {
  const bsvPayments: BSVPayments = {
    requestPayment,
    hasPaidForAction,
    getPaymentAmount,
    isWalletConnected: () => false,
    getWalletAddress: () => null,
    checkBalance: async () => {}
  };

  window.bsvPayments = bsvPayments;
}

